import { type CatalogVariant, formatBRL } from '@deizi/shared';
import { useState } from 'react';
import { Link } from 'react-router';
import { Modal } from '../../components/Overlays';
import { useCatalog } from '../../lib/products-api';
import { useDebounced } from '../../lib/use-debounced';

/**
 * Pick a catalog variant: "Começar de um produto" (Quick Quote) and "Adicionar item" (quote,
 * board Orcamento-Detalhe). Cost and suggested price on `channelId` with today's costs and fees.
 */
export function CatalogDialog({
  title,
  intro,
  channelId,
  priceLabel,
  actionLabel,
  productId,
  pending,
  onPick,
  onClose,
}: {
  title: string;
  intro: string;
  channelId: string | null;
  priceLabel: string;
  actionLabel: string;
  /** Show only this product's variants (from "Orçar com este produto"). */
  productId?: string | null;
  pending?: boolean;
  onPick: (item: CatalogVariant) => void;
  onClose: () => void;
}) {
  const [q, setQ] = useState('');
  const debounced = useDebounced(q, 250);
  const catalog = useCatalog(channelId, debounced);
  const rows = (catalog.data?.data ?? []).filter((r) => !productId || r.product.id === productId);
  const cols = '1fr 96px 96px 112px';

  return (
    <Modal
      title={title}
      wide
      onClose={onClose}
      footer={
        <button type="button" className="btn" onClick={onClose}>
          Fechar
        </button>
      }
    >
      <p className="sm muted">{intro}</p>
      {productId ? null : (
        <input
          className="input"
          type="search"
          aria-label="Buscar produto"
          placeholder="Buscar produto ou SKU"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      )}
      <div className="card flat" style={{ maxHeight: 420, overflowY: 'auto' }}>
        <div className="gt">
          <div className="gtr head" style={{ gridTemplateColumns: cols }}>
            <span>Produto</span>
            <span className="num">Custo</span>
            <span className="num">{priceLabel}</span>
            <span />
          </div>
          {catalog.isPending ? (
            <div className="gtr" style={{ gridTemplateColumns: '1fr' }}>
              <span className="sm muted">Carregando…</span>
            </div>
          ) : null}
          {rows.map((r) => {
            const price = r.listedPriceCentavos ?? r.suggestedPriceCentavos;
            return (
              <div key={r.variant.id} className="gtr" style={{ gridTemplateColumns: cols }}>
                <span className="row" style={{ gap: 10, minWidth: 0 }}>
                  {r.coverUrl ? (
                    <img
                      src={r.coverUrl}
                      alt=""
                      style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 8 }}
                    />
                  ) : null}
                  <span style={{ minWidth: 0 }}>
                    <span style={{ fontWeight: 500, display: 'block' }} className="trunc">
                      {r.product.name} · {r.variant.name}
                    </span>
                    <span className="xs muted">
                      {r.variant.sku}
                      {r.product.status === 'prototype' ? ' · protótipo' : ''}
                      {r.missing ? ` · ${r.missing}` : ''}
                    </span>
                  </span>
                </span>
                <span className="num">
                  {r.unitCostCentavos !== null ? formatBRL(r.unitCostCentavos) : '—'}
                </span>
                <span
                  className="num"
                  title={r.listedPriceCentavos !== null ? 'Preço anunciado' : 'Preço sugerido'}
                >
                  {price !== null ? formatBRL(price) : '—'}
                </span>
                <span style={{ textAlign: 'right' }}>
                  <button
                    type="button"
                    className="btn sm"
                    disabled={!!r.missing || pending}
                    onClick={() => onPick(r)}
                  >
                    {actionLabel}
                  </button>
                </span>
              </div>
            );
          })}
          {catalog.isSuccess && rows.length === 0 ? (
            <div className="gtr" style={{ gridTemplateColumns: '1fr' }}>
              <span className="sm muted">
                {q.trim()
                  ? 'Nada com essa busca.'
                  : 'Nenhum produto ativo ou em protótipo com perfil de impressão ainda. '}
                {q.trim() ? null : <Link to="/produtos">Abrir produtos</Link>}
              </span>
            </div>
          ) : null}
        </div>
      </div>
    </Modal>
  );
}
